/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMachine } from '@xstate/react';
import { APIConstants } from '../../../../../../constants/APIConstants';
import { useDebounced } from '../../../../shared';
import { quarantineExportUrl } from './quarantine.api';
import { quarantineListMachine } from './machines/quarantineListMachine';
import type { FilterSection, QuarantineSortField } from './quarantine.types';

export function useQuarantineList() {
  const [state, send] = useMachine(quarantineListMachine);
  const { query, data, error, summary } = state.context;

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, APIConstants.DEBOUNCE_DELAY);
  useEffect(() => {
    send({ type: 'SET_SEARCH', search: debouncedSearch.trim() });
  }, [debouncedSearch, send]);

  const clearAll = useCallback(() => {
    setSearchInput('');
    send({ type: 'CLEAR_ALL' });
  }, [send]);

  return {
    hasData: data !== null,
    items: data?.results ?? [],
    total: data?.total ?? 0,
    truncated: data?.truncated ?? false,
    limit: data?.limit ?? 0,
    facets: data?.facets,
    query,
    loading: state.matches('loading'),
    failed: state.matches('failed'),
    error,
    activeCount: summary?.activeCount ?? null,
    activeCountTruncated: summary?.truncated ?? false,
    searchInput,
    exportUrl: quarantineExportUrl(query),
    hasActiveFilters: query.search !== '' || Object.values(query.filters).some((values) => values.length > 0),
    setSearchInput,
    setFilter: useCallback(
      (section: FilterSection, values: string[]) => send({ type: 'SET_FILTER', section, values }),
      [send]
    ),
    clearFilters: useCallback(() => send({ type: 'CLEAR_FILTERS' }), [send]),
    clearAll,
    setSort: useCallback(
      (sortBy: QuarantineSortField, asc: boolean) => send({ type: 'SET_SORT', sortBy, asc }),
      [send]
    ),
    setPage: useCallback((page: number) => send({ type: 'SET_PAGE', page }), [send]),
    setPageSize: useCallback((pageSize: number) => send({ type: 'SET_PAGE_SIZE', pageSize }), [send]),
    retry: useCallback(() => send({ type: 'RETRY' }), [send]),
  };
}
